import { z } from 'zod';

// Browser-safe contracts of the Maintenance module (zod only).

export const Trade = z.enum([
  'general', 'plumbing', 'electrical', 'hvac', 'roofing', 'elevator', 'fire_safety', 'envelope',
  'landscaping', 'snow_removal', 'cleaning', 'pest_control', 'locksmith', 'painting', 'professional', 'other',
]);
export const AssetCategory = z.enum([
  'structure', 'envelope', 'roofing', 'plumbing', 'electrical', 'hvac', 'elevator', 'fire_safety', 'parking', 'exterior', 'common_areas', 'other',
]);
export const AssetCondition = z.enum(['good', 'fair', 'poor', 'unknown']);
export const WorkOrderStatus = z.enum(['open', 'assigned', 'scheduled', 'completed', 'cancelled']);
export const WorkOrderPriority = z.enum(['normal', 'urgent']);
export const TaskKind = z.enum(['legal', 'insurance', 'preventive']);
export const LogbookKind = z.enum(['inspection', 'maintenance', 'repair', 'replacement', 'study', 'correction', 'other']);

const text = (max: number) => z.string().trim().min(1).max(max);
const optionalText = (max: number) => z.string().trim().max(max).optional().transform((v) => (v ? v : undefined));
const cents = z.number().int().min(0).max(1_000_000_000_00);
const date = z.iso.date();

export const VendorInput = z.object({
  name: text(200),
  trade: Trade,
  contactName: optionalText(200),
  email: z.union([z.email(), z.literal('')]).optional().transform((v) => v || undefined),
  phone: optionalText(50),
  rbqLicence: optionalText(30),
  insuranceExpiresOn: date.optional(),
  notes: optionalText(2000),
  active: z.boolean().default(true),
});
export type VendorInput = z.input<typeof VendorInput>;

export const AssetInput = z.object({
  name: text(200),
  category: AssetCategory,
  buildingId: z.uuid().optional(),
  location: optionalText(200),
  installedOn: date.optional(),
  expectedLifeYears: z.number().int().min(1).max(150).optional(),
  replacementCostCents: cents.optional(),
  condition: AssetCondition.default('unknown'),
  notes: optionalText(2000),
});
export type AssetInput = z.input<typeof AssetInput>;

export const CreateWorkOrder = z.object({
  title: text(200),
  description: z.string().trim().max(5000).default(''),
  priority: WorkOrderPriority.default('normal'),
  assetId: z.uuid().optional(),
  vendorId: z.uuid().optional(),
  requestId: z.uuid().optional(),
  scheduledOn: date.optional(),
  estimateCents: cents.optional(),
});
export type CreateWorkOrder = z.input<typeof CreateWorkOrder>;

/** Changes to an open work order. `null` clears a field. Completion has its own operation. */
export const UpdateWorkOrder = z.object({
  title: text(200).optional(),
  description: z.string().trim().max(5000).optional(),
  priority: WorkOrderPriority.optional(),
  status: z.enum(['open', 'assigned', 'scheduled', 'cancelled']).optional(),
  assetId: z.uuid().nullable().optional(),
  vendorId: z.uuid().nullable().optional(),
  scheduledOn: date.nullable().optional(),
  estimateCents: cents.nullable().optional(),
});
export type UpdateWorkOrder = z.input<typeof UpdateWorkOrder>;

export const CompleteWorkOrder = z.object({
  completedOn: date,
  actualCostCents: cents.optional(),
  note: optionalText(5000),
  logbookKind: LogbookKind.default('repair'),
});
export type CompleteWorkOrder = z.input<typeof CompleteWorkOrder>;

export const TaskInput = z.object({
  kind: TaskKind,
  title: text(200),
  description: z.string().trim().max(2000).default(''),
  reference: optionalText(200),
  assetId: z.uuid().optional(),
  everyMonths: z.number().int().min(1).max(240).optional(),
  nextDueOn: date.optional(),
  active: z.boolean().default(true),
});
export type TaskInput = z.input<typeof TaskInput>;

export const UpdateTask = TaskInput.partial().extend({ nextDueOn: date.nullable().optional(), everyMonths: z.number().int().min(1).max(240).nullable().optional() });
export type UpdateTask = z.input<typeof UpdateTask>;

export const CompleteTask = z.object({
  doneOn: date,
  note: optionalText(5000),
  costCents: cents.optional(),
  vendorId: z.uuid().optional(),
});
export type CompleteTask = z.input<typeof CompleteTask>;

export const LogbookInput = z.object({
  entryDate: date,
  kind: LogbookKind,
  title: text(200),
  description: z.string().trim().max(5000).default(''),
  assetId: z.uuid().optional(),
  vendorId: z.uuid().optional(),
  costCents: cents.optional(),
});
export type LogbookInput = z.input<typeof LogbookInput>;

export interface Vendor {
  id: string;
  name: string;
  trade: z.infer<typeof Trade>;
  contactName: string | null;
  email: string | null;
  phone: string | null;
  rbqLicence: string | null;
  insuranceExpiresOn: string | null;
  notes: string | null;
  active: boolean;
}

export interface Asset {
  id: string;
  name: string;
  category: z.infer<typeof AssetCategory>;
  buildingId: string | null;
  location: string | null;
  installedOn: string | null;
  expectedLifeYears: number | null;
  replacementCostCents: number | null;
  condition: z.infer<typeof AssetCondition>;
  notes: string | null;
  /** Year the component reaches the end of its expected life (installation year + expected life). */
  endOfLifeYear: number | null;
}

export interface WorkOrder {
  id: string;
  number: number;
  title: string;
  description: string;
  priority: z.infer<typeof WorkOrderPriority>;
  status: z.infer<typeof WorkOrderStatus>;
  assetId: string | null;
  assetName: string | null;
  vendorId: string | null;
  vendorName: string | null;
  vendorEmail: string | null;
  requestId: string | null;
  scheduledOn: string | null;
  completedOn: string | null;
  estimateCents: number | null;
  actualCostCents: number | null;
  completionNote: string | null;
  createdAt: string;
}

export type TaskStatus = 'overdue' | 'due_soon' | 'ok' | 'unscheduled' | 'inactive';

export interface Task {
  id: string;
  kind: z.infer<typeof TaskKind>;
  templateCode: string | null;
  title: string;
  description: string;
  reference: string | null;
  assetId: string | null;
  assetName: string | null;
  everyMonths: number | null;
  nextDueOn: string | null;
  lastDoneOn: string | null;
  active: boolean;
  status: TaskStatus;
  /** Days until the due date (negative when overdue), null when unscheduled. */
  daysLeft: number | null;
}

export interface LogbookEntry {
  id: string;
  entryDate: string;
  kind: z.infer<typeof LogbookKind>;
  title: string;
  description: string;
  assetId: string | null;
  assetName: string | null;
  vendorName: string | null;
  workOrderId: string | null;
  workOrderNumber: number | null;
  costCents: number | null;
  recordedByName: string | null;
  createdAt: string;
}

export interface MaintenanceSummary {
  /** Whether the syndicate has any active obligation in its calendar. */
  calendarSetUp: boolean;
  overdueTasks: number;
  dueSoonTasks: number;
  unscheduledTasks: number;
  openWorkOrders: number;
  urgentWorkOrders: number;
  /** Open work orders the vendor reported done or declined (through their link): the board's turn. */
  vendorReports: number;
  /** Supplier invoices waiting for the board's decision. */
  invoicesToApprove: number;
  /** Invoices received by email not yet tied to a work order. */
  invoicesToFile: number;
  /** Work orders without a contractor that have quotes received. */
  quotesToCompare: number;
  /** Active vendors whose insurance certificate has expired or expires within 30 days. */
  vendorInsuranceAlerts: number;
  /** The next few obligations, overdue first. */
  upcoming: Pick<Task, 'id' | 'title' | 'kind' | 'nextDueOn' | 'status' | 'daysLeft'>[];
}

/** Within this many days, an obligation is "due soon". */
export const DUE_SOON_DAYS = 60;

/**
 * Reminder stages of an obligation, by days before its due date: 1 at 60 days, 2 at 30, 3 at 7, 4 once overdue.
 * The board is reminded once per stage; a new due date starts over.
 */
export function reminderStage(daysLeft: number | null): number {
  if (daysLeft === null) return 0;
  if (daysLeft < 0) return 4;
  if (daysLeft <= 7) return 3;
  if (daysLeft <= 30) return 2;
  if (daysLeft <= 60) return 1;
  return 0;
}

export interface ObligationToRemind extends Pick<Task, 'id' | 'title' | 'kind' | 'nextDueOn' | 'daysLeft' | 'reference'> {
  stage: number;
}

// --- Contractor access (secure link per work order)

export const WorkOrderEventKind = z.enum(['accepted', 'declined', 'date_proposed', 'note', 'file', 'done']);
export const VendorEventInput = z.object({
  kind: z.enum(['accepted', 'declined', 'date_proposed', 'note', 'done']),
  note: z.string().trim().max(3000).default(''),
  proposedDate: z.iso.date().optional(),
});
export type VendorEventInput = z.input<typeof VendorEventInput>;

export interface WorkOrderEvent {
  id: string;
  source: 'vendor' | 'board';
  kind: z.infer<typeof WorkOrderEventKind>;
  note: string;
  proposedDate: string | null;
  fileName: string | null;
  contentType: string | null;
  sizeBytes: number | null;
  createdAt: string;
}

export interface VendorLinkStatus {
  active: { createdAt: string; expiresAt: string; lastUsedAt: string | null } | null;
}

/** What a contractor sees through the link: the job, not the syndicate's records. */
export interface VendorWorkOrderView extends VendorPurchasing {
  number: number;
  title: string;
  description: string;
  priority: 'normal' | 'urgent';
  status: WorkOrder['status'];
  scheduledOn: string | null;
  assetName: string | null;
  location: string | null;
  vendorName: string | null;
  events: WorkOrderEvent[];
}

// --- Supplier invoices and quotes

/** Quebec sales taxes: GST 5 %, QST 9.975 % (both on the subtotal). */
export const GST_RATE = 0.05;
export const QST_RATE = 0.09975;
/** An invoice whose subtotal exceeds the authorized estimate by more than this is flagged. */
export const OVER_ESTIMATE_TOLERANCE = 0.1;

export const InvoiceIssueCode = z.enum([
  'vendor_mismatch', // not the contractor assigned to the work order
  'vendor_name_mismatch', // the name on the invoice does not look like the vendor's
  'not_completed', // the work order is not completed
  'no_estimate', // nothing was authorized to compare with
  'over_estimate', // invoices for this work order exceed the estimate (taxes excluded) by more than 10 %
  'duplicate_number', // the same vendor already sent this invoice number
  'tax_arithmetic', // GST/QST do not match the subtotal
  'total_arithmetic', // subtotal + taxes ≠ total
  'unread', // no text could be read: the amounts were entered by hand
  'missing_total', // the total could not be read: to enter before approving
]);
export type InvoiceIssueCode = z.infer<typeof InvoiceIssueCode>;
export interface InvoiceIssue {
  code: InvoiceIssueCode;
  /** Amount in cents when relevant (excess over the estimate, expected taxes). */
  amountCents?: number;
}

/** What the agent read on an invoice (it proposes; the board checks). */
export interface InvoiceReading {
  vendorName: string;
  invoiceNumber: string;
  invoiceDate: string | null;
  subtotalCents: number | null;
  gstCents: number | null;
  qstCents: number | null;
  totalCents: number | null;
  /** A work order, purchase order or other reference the customer gave, as printed. */
  reference?: string;
  /** Whether the document is an invoice at all (not a quote, a statement or terms). */
  isInvoice?: boolean;
  confidence: 'high' | 'medium' | 'low';
}

/** Mailbox of each client that receives contractors' invoices: factures@<slug>.<domain>. */
export const INVOICE_MAILBOX = 'factures';
export const invoiceAddress = (slug: string, baseDomain: string) => `${INVOICE_MAILBOX}@${slug}.${baseDomain}`;

/** A work order an unsorted invoice may belong to, with why. */
export interface InvoiceSuggestion {
  workOrderId: string;
  number: number;
  title: string;
  vendorName: string | null;
  reason: 'vendor' | 'amount' | 'recent';
}

export const InvoiceStatus = z.enum(['received', 'approved', 'rejected']);
export interface Invoice {
  id: string;
  /** Null: received by email and not sorted yet. */
  workOrderId: string | null;
  workOrderNumber: number | null;
  workOrderTitle: string | null;
  vendorId: string | null;
  vendorName: string | null;
  source: 'vendor' | 'board' | 'email';
  fromEmail: string | null;
  emailSubject: string | null;
  /** How it was tied to its work order: number on the invoice, the vendor's only job, or by the board. */
  matchedBy: 'number' | 'only_job' | 'board' | null;
  suggestions: InvoiceSuggestion[];
  invoiceNumber: string;
  invoiceDate: string | null;
  subtotalCents: number | null;
  taxesCents: number | null;
  totalCents: number | null;
  fileName: string;
  contentType: string;
  extracted: InvoiceReading | null;
  issues: InvoiceIssue[];
  status: z.infer<typeof InvoiceStatus>;
  decidedAt: string | null;
  decisionNote: string;
  ledgerEntryId: string | null;
  receivedAt: string;
}

export const InvoiceDetails = z.object({
  invoiceNumber: z.string().trim().max(60).default(''),
  invoiceDate: date.nullable().default(null),
  subtotalCents: cents.nullable().default(null),
  taxesCents: cents.nullable().default(null),
  totalCents: cents.refine((v) => v > 0, 'The total must be positive'),
});
export type InvoiceDetails = z.input<typeof InvoiceDetails>;

/** Approving records the expense (account and fund chosen by the board) and the amount owed to the vendor. */
export const ApproveInvoice = z.object({
  accountNumber: z.string().regex(/^\d{4}$/).default('5330'),
  fundCode: z.string().trim().min(1).max(10).default('OP'),
  note: z.string().trim().max(500).default(''),
});
export const RejectInvoice = z.object({ note: z.string().trim().min(3).max(500) });

const normalize = (s: string) =>
  s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/\b(inc|ltee|ltd|enr|senc|cie|les|des|du|de|la|le)\b/g, ' ')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

/** Whether two business names plausibly name the same company (a shared significant word). */
export function sameBusiness(a: string, b: string): boolean {
  const words = (s: string) => new Set(normalize(s).split(' ').filter((w) => w.length >= 3));
  const wa = words(a);
  return [...words(b)].some((w) => wa.has(w));
}

/** Expected GST + QST on a subtotal, in cents. */
export const expectedTaxesCents = (subtotalCents: number) => Math.round(subtotalCents * GST_RATE) + Math.round(subtotalCents * QST_RATE);

/**
 * Checks an invoice against its work order (pure: shared with the web app). The board decides; these are flags.
 * The estimate is before taxes (as in the not-to-exceed amount given to the vendor).
 */
export function checkInvoice(input: {
  invoice: { vendorId: string | null; invoiceNumber: string; subtotalCents: number | null; taxesCents: number | null; totalCents: number | null };
  reading: InvoiceReading | null;
  workOrder: { vendorId: string | null; vendorName: string | null; status: WorkOrder['status']; estimateCents: number | null };
  /** The work order's other invoices (not this one), and the same vendor's invoice numbers elsewhere. */
  others: { invoiceNumber: string; vendorId: string | null; subtotalCents: number | null; totalCents: number | null; status: 'received' | 'approved' | 'rejected'; sameWorkOrder: boolean }[];
}): InvoiceIssue[] {
  const { invoice, reading, workOrder, others } = input;
  const issues: InvoiceIssue[] = [];
  if (invoice.vendorId && workOrder.vendorId && invoice.vendorId !== workOrder.vendorId) issues.push({ code: 'vendor_mismatch' });
  if (reading?.vendorName && workOrder.vendorName && !sameBusiness(reading.vendorName, workOrder.vendorName)) issues.push({ code: 'vendor_name_mismatch' });
  if (workOrder.status !== 'completed') issues.push({ code: 'not_completed' });
  const preTax = (i: { subtotalCents: number | null; totalCents: number | null }) => i.subtotalCents ?? Math.round((i.totalCents ?? 0) / (1 + GST_RATE + QST_RATE));
  if (invoice.totalCents === null) issues.push({ code: 'missing_total' });
  if (workOrder.estimateCents === null) issues.push({ code: 'no_estimate' });
  else if (invoice.totalCents !== null) {
    const invoiced = preTax(invoice) + others.filter((o) => o.sameWorkOrder && o.status !== 'rejected').reduce((sum, o) => sum + preTax(o), 0);
    const allowed = Math.round(workOrder.estimateCents * (1 + OVER_ESTIMATE_TOLERANCE));
    if (invoiced > allowed) issues.push({ code: 'over_estimate', amountCents: invoiced - workOrder.estimateCents });
  }
  const number = invoice.invoiceNumber.trim().toLowerCase();
  if (number && others.some((o) => o.status !== 'rejected' && o.vendorId === invoice.vendorId && o.invoiceNumber.trim().toLowerCase() === number)) issues.push({ code: 'duplicate_number' });
  if (invoice.subtotalCents !== null && invoice.taxesCents !== null && invoice.taxesCents > 0) {
    const expected = expectedTaxesCents(invoice.subtotalCents);
    if (Math.abs(invoice.taxesCents - expected) > 2) issues.push({ code: 'tax_arithmetic', amountCents: expected });
  }
  if (invoice.totalCents !== null && invoice.subtotalCents !== null && invoice.taxesCents !== null && Math.abs(invoice.subtotalCents + invoice.taxesCents - invoice.totalCents) > 1) issues.push({ code: 'total_arithmetic' });
  if (!reading) issues.push({ code: 'unread' });
  return issues;
}

export const QuoteStatus = z.enum(['requested', 'received', 'declined_by_vendor', 'selected', 'not_selected']);
/** What the agent read on a quote. */
export interface QuoteReading {
  subtotalCents: number | null;
  totalCents: number | null;
  validUntil: string | null;
  delayDays: number | null;
  scope: string;
  exclusions: string[];
  confidence: 'high' | 'medium' | 'low';
}
export interface Quote {
  id: string;
  workOrderId: string;
  vendorId: string;
  vendorName: string;
  vendorEmail: string | null;
  status: z.infer<typeof QuoteStatus>;
  amountCents: number | null;
  delayDays: number | null;
  validUntil: string | null;
  note: string;
  fileName: string | null;
  extracted: QuoteReading | null;
  requestedAt: string;
  receivedAt: string | null;
  decidedAt: string | null;
}

export const RequestQuotes = z.object({
  vendorIds: z.array(z.uuid()).min(1).max(5),
  dueOn: date.optional(),
});
export const SubmitQuote = z.object({
  amountCents: cents.refine((v) => v > 0, 'The amount must be positive'),
  delayDays: z.number().int().min(0).max(365).nullable().default(null),
  validUntil: date.nullable().default(null),
  note: z.string().trim().max(2000).default(''),
});
export type SubmitQuote = z.input<typeof SubmitQuote>;

/** What a contractor sees about their own invoices and quote through the link. */
export interface VendorPurchasing {
  purpose: 'work' | 'quote';
  quote: Pick<Quote, 'status' | 'amountCents' | 'delayDays' | 'validUntil' | 'note' | 'fileName'> | null;
  invoices: Pick<Invoice, 'id' | 'invoiceNumber' | 'totalCents' | 'status' | 'decisionNote' | 'receivedAt'>[];
  /** Where to email invoices (null when email intake is not configured). */
  invoiceAddress?: string | null;
}

/** A reference to one of the client's work orders, as contractors write it on invoices ("bon de travail no 12", "BT-12", "PO 12"…). */
export function workOrderNumberIn(text: string): number | null {
  const m = /(?:bon\s+de\s+travail|bon\s+de\s+commande|\bb\.?\s?t\.?|\bp\.?\s?o\.?|work\s+order|\bw\.?\s?o\.?)\s*(?:no\.?|n[°ºo]\.?|#|:|-)?\s*(\d{1,6})\b/i.exec(text);
  return m ? Number(m[1]) : null;
}

const FREE_MAIL = ['gmail.com', 'hotmail.com', 'hotmail.ca', 'outlook.com', 'live.com', 'live.ca', 'yahoo.com', 'yahoo.ca', 'icloud.com', 'me.com', 'videotron.ca', 'bell.net', 'sympatico.ca', 'msn.com', 'aol.com', 'protonmail.com', 'cogeco.ca'];
/** The company domain of an address, or null for a public mailbox (gmail, hotmail, videotron…). */
export function businessDomain(email: string): string | null {
  const domain = email.split('@')[1]?.toLowerCase().trim();
  return domain && !FREE_MAIL.includes(domain) ? domain : null;
}
